import React, { useEffect, useMemo, useRef, useState } from 'react';
import { RefreshCw } from 'lucide-react';
import {
  MEASUREMENT_ORDERING_HINT,
  measurementKetLabel,
} from '../constants/measurementProbabilities';
import './QuantumSimulator.css';

const SIMULATOR_PATH = '/quantum_simulator/out/quirk.html';
/** Embed mode matches ProblemDetail iframe: flex layout + scaled canvas inside the iframe. */
const SIMULATOR_IFRAME_SRC = (frameKey) =>
  `${SIMULATOR_PATH}?embed=1&standalone=1&v=${encodeURIComponent(String(frameKey))}`;

const ProbabilityChart = ({ numWires, probabilities }) => {
  if (!probabilities || probabilities.length === 0) {
    return (
      <div className="probability-chart probability-chart--empty">
        <div className="probability-chart__title-row">
          <p className="probability-chart__title">Measurement Probabilities</p>
          <p className="probability-chart__ordering">{MEASUREMENT_ORDERING_HINT}</p>
        </div>
        <p className="probability-chart__hint">
          Build a circuit to see the probability of each computational basis state.
        </p>
      </div>
    );
  }

  return (
    <div className="probability-chart">
      <div className="probability-chart__heading">
        <div className="probability-chart__title-row">
          <p className="probability-chart__title">Measurement Probabilities</p>
          <p className="probability-chart__ordering">{MEASUREMENT_ORDERING_HINT}</p>
        </div>
        <p className="probability-chart__subtitle">
          {numWires} qubit{numWires === 1 ? '' : 's'} &middot; {probabilities.length} state
          {probabilities.length === 1 ? '' : 's'}
        </p>
      </div>
      <div className="probability-chart__bars">
        {probabilities.map((prob, i) => {
          const clamped = Math.min(Math.max(prob, 0), 1);
          const percent = (clamped * 100).toFixed(clamped > 0.0999 ? 1 : 2);
          return (
            <div className="probability-chart__row" key={i}>
              <span className="probability-chart__label">{measurementKetLabel(i, numWires)}</span>
              <div className="probability-chart__track">
                <div
                  className="probability-chart__fill"
                  style={{ width: `${clamped * 100}%` }}
                />
              </div>
              <span className="probability-chart__value">{percent}%</span>
            </div>
          );
        })}
      </div>
    </div>
  );
};

const QuantumSimulator = () => {
  const [frameKey, setFrameKey] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [hasError, setHasError] = useState(false);
  const [probabilityData, setProbabilityData] = useState({ numWires: 0, probabilities: [] });
  const iframeRef = useRef(null);
  const iframeSrc = useMemo(() => SIMULATOR_IFRAME_SRC(frameKey), [frameKey]);

  const reloadSimulator = () => {
    setHasError(false);
    setIsLoading(true);
    setProbabilityData({ numWires: 0, probabilities: [] });
    setFrameKey((prev) => prev + 1);
  };

  useEffect(() => {
    const handleMessage = (event) => {
      const data = event.data;
      if (!data || data.type !== 'quirk-probabilities') {
        return;
      }
      if (iframeRef.current && event.source !== iframeRef.current.contentWindow) {
        return;
      }
      setProbabilityData({
        numWires: data.numWires,
        probabilities: Array.isArray(data.probabilities) ? data.probabilities : [],
      });
    };
    window.addEventListener('message', handleMessage);
    return () => window.removeEventListener('message', handleMessage);
  }, []);

  return (
    <section className="quantum-simulator">
      <header className="quantum-simulator__header">
        <div className="quantum-simulator__actions">
          <button type="button" className="simulator-button simulator-button--ghost" onClick={reloadSimulator}>
            <RefreshCw size={14} />
            Reload
          </button>
        </div>
      </header>

      <div className="quantum-simulator__frame-shell">
        {isLoading && !hasError && (
          <div className="quantum-simulator__overlay">
            <div className="quantum-simulator__spinner" aria-hidden="true" />
            <p>Loading simulator...</p>
          </div>
        )}

        {hasError && (
          <div className="quantum-simulator__overlay quantum-simulator__overlay--error">
            <p>Unable to load the simulator.</p>
            <button type="button" className="simulator-button" onClick={reloadSimulator}>
              Try Again
            </button>
          </div>
        )}

        <div className="quantum-simulator__iframe-wrap">
          <iframe
            ref={iframeRef}
            key={iframeSrc}
            title="Quantum Circuit Simulator"
            className="quantum-simulator__iframe"
            src={iframeSrc}
            onLoad={() => setIsLoading(false)}
            onError={() => {
              setIsLoading(false);
              setHasError(true);
            }}
            allowFullScreen
          />
        </div>
      </div>

      <ProbabilityChart
        numWires={probabilityData.numWires}
        probabilities={probabilityData.probabilities}
      />
    </section>
  );
};

export default QuantumSimulator;
